import React, { Suspense, lazy, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { HashRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { CenterToaster, notify } from './components/CenterToaster';
import TooltipLayer from './components/common/TooltipLayer';
import { authService } from './services/auth.service';
import { API_BASE_URL } from './services/api.client';
import { isUnavailabilityError, reportServerReachable, reportServerUnreachable } from './services/serverMonitor';
import { clientLicenseService, getCachedCheckResult, invalidateLicenseCheckCache } from './services/license.service';
import { PermissionProvider } from './hooks/usePermissions';
import { FeatureProvider } from './hooks/useFeatures';
import { ThemeProvider } from './contexts/ThemeContext';
import { AttendanceCodeProvider } from './contexts/AttendanceCodeContext';
import './App.css';
import ElectEmb from './components/ElectEmb';
import IdleTimeout from './components/IdleTimeout';
import { rehydrateSessionStateAsync, rehydratePlatformSessionAsync, clearSession, type RehydrateResult } from './services/auth.service';
import { loadPlatformPreferences } from './services/platformPreferences';
import { restoreContextFromPreferences } from './hooks/useAppContext';
import { mayHaveHrmsSession, mayHavePlatformSession } from './services/sessionPresence';
import { loadPreferences as loadPrefs, clearPreferences } from './services/preferences';
import { loadDashboard } from './preload';

const Login = lazy(() => import('./components/Login'));
const PlatformLogin = lazy(() => import('./components/PlatformLogin'));
const PlatformDashboard = lazy(() => import('./components/PlatformDashboard'));
const ForgotPassword = lazy(() => import('./components/ForgotPassword'));
const ResetPassword = lazy(() => import('./components/ResetPassword'));
const TrackDuty = lazy(() => import('./components/TrackDuty'));
const EssPayslip = lazy(() => import('./components/EssPayslip'));
const ActivateAccount = lazy(() => import('./components/ActivateAccount'));
const Dashboard = lazy(loadDashboard);
const LicenseActivation = lazy(() => import('./components/LicenseActivation'));


/**
 * Establish the session from the server before anything guarded renders.
 *
 * Nothing about the session is kept in the browser: the credential is an HttpOnly cookie the page
 * cannot read, and identity, expiry, context and preferences live in memory. So on every load the
 * app asks /auth/me (the cookie goes with it), then loads the preference bundle and restores the
 * context the person last worked in. A 401 simply means no session, and the guards below send them
 * to login. Until the answer arrives the guards would say "signed out" for a signed-in user, which
 * is why nothing guarded renders before it does.
 */
/**
 * Boot retry cadence while the API is down — a BOUNDED budget, not a loop.
 *
 * It adds up to the same 5 s of continuous failure that serverMonitor gives live traffic before it
 * logs the session out, so boot and the running app agree on when the backend counts as gone. It
 * used to widen forever (2 s, 4 s, 8 s, then every 15 s), which meant a page opened against a dead
 * API sat on "Waiting for the server…" and re-asked /auth/me for as long as it was left open —
 * every attempt a failed request in the console. When this budget runs out the session is dropped
 * and the login page is shown, exactly as an outage mid-session already does.
 */
const BOOT_RETRY_MS = [1_000, 2_000, 2_000];
const wait = (ms: number) => new Promise<void>(r => setTimeout(r, ms));

/**
 * Learns who the cookie says we are before any route renders.
 *
 * It asks whenever there MIGHT be a session to ask about. The server leaves a readable marker
 * beside each HttpOnly session cookie, and a browser holding neither goes straight to the routes
 * with no request made — a sign-out is not an error, and must not look like one in the console.
 * Where the API answers on a different hostname than the page, though, that marker cannot be read
 * at all (see sessionPresence), and only the server knows; there, boot always asks.
 *
 * A server that is not answering is not immediately a sign-out: the cookie is still there, and a
 * restart takes a moment. So an unreachable server is retried, but only within a bounded budget
 * (BOOT_RETRY_MS) matching the one serverMonitor gives live traffic. When that runs out the session
 * is dropped and the login page shown — a dead API must not leave the app retrying for as long as
 * the tab stays open. Only a real answer of "no session" goes on to ask about the platform cookie —
 * there is no point asking a second endpoint on a port that just refused the first.
 *
 * Nothing here waits on anything it does not have to. The identity, the preference bundle and the
 * Dashboard chunk are all requested at once — the cookie authenticates the first two and the third
 * is a static file — and the providers above this component (permissions, features, attendance
 * codes, theme) are told the session exists the moment it does, so their requests overlap the
 * Dashboard's first render instead of following it. They had been fetching before the session was
 * restored, finding none, and never being told otherwise: every reload — and a mode switch is a
 * reload — came up with an empty permission set until something else nudged them.
 */
export const SessionBootstrap: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [ready, setReady] = useState(false);
  const [waiting, setWaiting] = useState(false);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const hrms = mayHaveHrmsSession();
      if (hrms) loadDashboard().catch(() => { /* the router's lazy() will report it */ });

      // THE IDENTITY GOES FIRST, AND ALONE.
      //
      // The preference bundle used to be requested alongside it, to save a round trip. But the
      // bundle is COMPANY-SCOPED, and the request interceptor stamps X-Company-Id from the context
      // in memory — which at this instant is empty. So the bundle went out with no company and the
      // server answered for the one the token was minted under, which for a multi-company account
      // is not where the user was working. The saved company then arrived a moment later and the
      // app corrected itself in front of them: the flicker, and a company-scoped bundle belonging
      // to the wrong company underneath it.
      //
      // rehydrateSessionStateAsync applies the context the SERVER resolved (adoptServerContext), so
      // waiting for it means every request after this line — the bundle included — carries the
      // right company from the very first one. One extra round trip at boot, and no window in which
      // the app is somewhere the user is not.
      let state: RehydrateResult = hrms ? await rehydrateSessionStateAsync() : 'none';
      for (let attempt = 0; state === 'unreachable' && attempt < BOOT_RETRY_MS.length && !cancelled; attempt++) {
        setWaiting(true);
        await wait(BOOT_RETRY_MS[attempt]);
        if (cancelled) return;
        state = await rehydrateSessionStateAsync();
      }
      if (cancelled) return;
      if (state === 'unreachable') {
        // The budget is spent and the backend is still not answering. Treat it the way an outage
        // mid-session is treated: drop the session and show the login page, with the reason on it.
        // Navigated by hash, never by reload — a reload would boot this component again and start
        // the same requests over, which is how a dead API turned into an endless console of them.
        clearSession();
        clearPreferences();
        const target = '#/login?reason=server_unavailable';
        if (window.location.hash !== target) window.location.hash = target;
      } else if (state === 'session') {
        await loadPrefs(true).catch(() => { /* defaults apply until the next load succeeds */ });
        if (cancelled) return;
        restoreContextFromPreferences();
        window.dispatchEvent(new Event('auth-changed'));
      } else {
        clearPreferences();
        if (mayHavePlatformSession() && await rehydratePlatformSessionAsync()) {
          await loadPlatformPreferences(true);
        }
      }
      if (!cancelled) setReady(true);
    })();
    return () => { cancelled = true; };
  }, []);
  if (!ready) return <ElectEmb mode="overlay" message={waiting ? 'Waiting for the server…' : 'Restoring session…'} />;
  return <>{children}</>;
};

const PrivateRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return authService.isAuthenticated() ? <>{children}</> : <Navigate to="/login" />;
};

const PlatformPrivateRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return authService.isPlatformAuthenticated() ? <>{children}</> : <Navigate to="/platform/login" />;
};

// Check every 12 hours to detect revocation/suspension while the app is open.
const HEARTBEAT_INTERVAL_MS = 12 * 60 * 60 * 1000;

/**
 * Periodically polls GET /api/license/check to detect revocation or suspension
 * while the user is logged in. On failure, invalidates the cache and redirects
 * to the license screen. Network errors are ignored (benefit of the doubt).
 */
const LicenseHeartbeat: React.FC = () => {
  const navigate = useNavigate();
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    const beat = async () => {
      if (window.location.hash.startsWith('#/platform')) return;
      const result = await clientLicenseService.check(true /* skipCache — always hit DB */);
      if (result !== null && !result.valid) {
        invalidateLicenseCheckCache();
        authService.logout?.();
        navigate('/license', { replace: true });
      }
    };

    timerRef.current = setInterval(beat, HEARTBEAT_INTERVAL_MS);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [navigate]);

  return null;
};

// Server reachability heartbeat. Live API traffic is monitored by the axios response
// interceptor, but an idle session (no request in flight) would not notice a stopped API.
// This polls GET /api/ping on a healthy cadence and feeds the result into the centralized
// serverMonitor, which owns the 5-second continuous-failure → auto-logout decision. Kept
// frequent enough that an idle session detects an outage promptly.
const HEARTBEAT_PING_MS = 3_000;    // healthy cadence
const HEARTBEAT_TIMEOUT_MS = 4_000; // per-ping timeout

/**
 * Pings the unauthenticated /api/ping endpoint while a user is logged in on a protected screen,
 * reporting reachability to the shared serverMonitor. Uses a bare axios call (no auth header)
 * so it never feeds itself through the interceptor. The monitor debounces a single blip and
 * only logs out after 5s of continuous failure.
 */
const ServerHeartbeat: React.FC = () => {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    let stopped = false;

    const schedule = (delay: number) => {
      if (stopped) return;
      timerRef.current = setTimeout(beat, delay);
    };

    const beat = async () => {
      // Only monitor a logged-in HRMS session on a protected screen.
      const hash = window.location.hash;
      if (!authService.isAuthenticated() ||
          hash.startsWith('#/login') || hash.startsWith('#/platform')) {
        reportServerReachable();
        schedule(HEARTBEAT_PING_MS);
        return;
      }

      try {
        await axios.get(`${API_BASE_URL}/ping`, { timeout: HEARTBEAT_TIMEOUT_MS });
        reportServerReachable();
      } catch (err) {
        if (isUnavailabilityError(err)) reportServerUnreachable();
        else reportServerReachable(); // a real HTTP response means the server is up
      }
      schedule(HEARTBEAT_PING_MS);
    };

    // Run the first check almost immediately so a server that is already down is caught fast.
    schedule(1_000);
    return () => { stopped = true; if (timerRef.current) clearTimeout(timerRef.current); };
  }, []);

  return null;
};

// The app is client-routed (HashRouter — see index.tsx): navigating between screens never
// re-fetches index.html or the JS/CSS bundle, so a tab left open across a deploy silently keeps
// running whatever build it originally loaded — including layout bugs a later build already
// fixed — until something forces a fresh load. This polls public/version.json (written fresh on
// every build by scripts/generate-version.js; nginx never caches it) and, once the build id it
// sees no longer matches the one this tab started with, warns the user and reloads shortly after
// so an in-progress edit isn't lost mid-keystroke.
const VERSION_POLL_MS = 5 * 60 * 1000;   // steady cadence while the tab is open
const VERSION_RELOAD_GRACE_MS = 15_000;  // window to notice the toast / finish typing before reload

/**
 * Detects a newer frontend build than the one this tab loaded and reloads to pick it up.
 * Silent while no new build is seen; on a mismatch, shows one toast and reloads once, after a
 * short grace period — matches VERSION_RELOAD_GRACE_MS above.
 */
const VersionWatcher: React.FC = () => {
  const baselineRef = useRef<string | null>(null);
  const reloadingRef = useRef(false);

  useEffect(() => {
    let stopped = false;

    const fetchBuildId = async (): Promise<string | null> => {
      try {
        const res = await fetch('/version.json', { cache: 'no-store' });
        if (!res.ok) return null;
        const data = await res.json();
        return typeof data?.buildId === 'string' ? data.buildId : null;
      } catch {
        return null; // offline / server unreachable — try again next tick
      }
    };

    const check = async () => {
      if (stopped || reloadingRef.current) return;
      const buildId = await fetchBuildId();
      if (!buildId) return;

      if (baselineRef.current === null) {
        baselineRef.current = buildId; // first successful fetch — this tab's starting build
        return;
      }

      if (buildId !== baselineRef.current) {
        reloadingRef.current = true;
        notify.info('A new version of Dexter is available.', 'Refreshing in 15 seconds to load it…');
        setTimeout(() => window.location.reload(), VERSION_RELOAD_GRACE_MS);
      }
    };

    check(); // establish the baseline as soon as the tab opens
    const interval = setInterval(check, VERSION_POLL_MS);

    // Also check right when the user comes back to this tab — the moment they're most likely
    // to actually see a stale layout, and a good moment to catch up before they resume working.
    const onVisibilityChange = () => { if (document.visibilityState === 'visible') check(); };
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      stopped = true;
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, []);

  return null;
};

/**
 * LicenseGate — server-authoritative license check before rendering any protected route.
 * Calls GET /api/license/check (with a 5-minute in-memory cache to avoid per-navigation
 * round trips). The server uses its own hardware fingerprint to query the DB — no
 * localStorage token, browser cache, or client-side state is involved.
 *
 *   valid → render children
 *   invalid → redirect to /license
 *   null (network error) → give benefit of the doubt, render children
 */
const LicenseGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Pre-populate from cache synchronously to avoid a loading flash on cached routes
  const cached = getCachedCheckResult();
  const [valid, setValid] = useState<boolean | null>(cached ? cached.valid : null);

  useEffect(() => {
    if (cached) return; // fresh cache hit — no network call needed
    clientLicenseService.check().then(result => {
      // null = server unreachable → give benefit of the doubt
      setValid(result === null || result.valid);
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (valid === null) return <ElectEmb mode="overlay" message="Verifying license…" />;
  if (!valid) return <Navigate to="/license" replace />;
  return <>{children}</>;
};

function App() {
  return (
    <Router>
      <ThemeProvider>
      <AttendanceCodeProvider>
      <PermissionProvider>
      <FeatureProvider>
      <div className="App">
        {/* Center-screen popup notifications (Success / Warning / Error / Information) for every
            react-hot-toast call across the app — dimmed backdrop, auto-close, manual close, responsive. */}
        <CenterToaster />
        {/* The one tooltip every `data-tip` in the app renders through — body-level, never clipped. */}
        <TooltipLayer />
        <IdleTimeout />
        <LicenseHeartbeat />
        <ServerHeartbeat />
        <VersionWatcher />
        <Suspense fallback={<ElectEmb mode="overlay" message="Loading…" />}>
        <SessionBootstrap>
        <Routes>
          {/* ── License activation — shown on first launch before login ── */}
          <Route path="/license" element={<LicenseActivation />} />
          {/* Public no-login GPS sharing page — the SMS/WhatsApp tracking link.
              The GUID token in the URL is the sole authorisation (see FieldDutyController). */}
          <Route path="/track/:token" element={<TrackDuty />} />
            {/* Public payslip self-service behind the Payroll → PayQR Generator QR (signed token in ?t=). */}
            <Route path="/ess/payslip" element={<EssPayslip />} />

          {/* ── Platform admin — completely independent of HRMS license ── */}
          <Route path="/platform/login" element={<PlatformLogin />} />
          <Route path="/platform" element={<Navigate to="/platform/dashboard" replace />} />
          <Route path="/platform/*" element={<PlatformPrivateRoute><PlatformDashboard /></PlatformPrivateRoute>} />

          {/* ── Pre-login routes — public (no LicenseGate needed; dashboard routes are protected) ── */}
          <Route path="/login"            element={<Login />} />
          <Route path="/forgot-password"  element={<ForgotPassword />} />
          <Route path="/reset-password"   element={<ResetPassword />} />
          <Route path="/activate-account" element={<ActivateAccount />} />

          {/* ── Protected app routes ── */}
          <Route
            path="/:view/*"
            element={
              <LicenseGate>
                <PrivateRoute>
                  <Dashboard />
                </PrivateRoute>
              </LicenseGate>
            }
          />
          <Route
            path="/dashboard"
            element={
              <LicenseGate>
                <PrivateRoute>
                  <Navigate to="/home" />
                </PrivateRoute>
              </LicenseGate>
            }
          />

          {/* ── Root: smart redirect — HRMS takes priority, then platform, then login ── */}
          <Route path="/" element={
            authService.isAuthenticated()
              ? <Navigate to="/home" replace />
              : authService.isPlatformAuthenticated()
                ? <Navigate to="/platform/dashboard" replace />
                : <Navigate to="/login" replace />
          } />
        </Routes>
        </SessionBootstrap>
        </Suspense>
      </div>
      </FeatureProvider>
      </PermissionProvider>
      </AttendanceCodeProvider>
      </ThemeProvider>
    </Router>
  );
}

export default App;
